import { useLocale } from "@/store/locale-store";
import type { CheckoutEntryPoint } from "@/libs/analytics/cart-trigger";
import { useState } from "react";
import { CHECKOUT_ERROR_CODE } from "@/constants/CheckoutErrorCodes";
import { analyticsInstance } from "@/libs/analytics";
import {
  useCartWithProducts,
  useReferralDiscount,
} from "@/store/useCartWithProducts";
import { isReferralActiveForProduct } from "@/hooks/useReferralDiscount";
import FbPixel from "@/utils/FbPixel";
import { ProductSlug } from "@/api/product-api";
import OrderService from "@/services/OrderService";
import { useCurrency } from "@/hooks/useCurrency";
import { resolveCheckoutToken } from "@/api/apis";
import { useRouter } from "next/router";
import { useQueryClient } from "@tanstack/react-query";
import { getCartState, useCartStore } from "@/store/cart-store";
import { useProducts } from "@/services/product/useProductService";
import { CartService } from "@/services/cart-service";
import { OrderDraft, OrderItemV2 } from "@/models/orders/OrderDraft";
import { OrderUtils } from "@/v2-components/pages/checkout/_utils/order-utils";
import { GameItem, ShippingType } from "@/models/orders/OrderModelV2";
import ShippingApi from "@/api/ShippingApi";
import { useBuyTactSuit } from "@/store/useBuyTactSuit";
import { ProductUtil } from "@/utils/ProductUtil";
import { packSetLines } from "@/libs/cart/pack-set-lines";
import { CartItem } from "@/store/cart-store/type";
import { isGeRoutedFor } from "@/libs/global-e/marker";
import { exceedsGeShippingWeight } from "@/libs/global-e/shipping-limit";
import { useOverweightDialogStore } from "@/store/overweight-dialog-store";
import {
  prepareGeCheckoutCoupons,
  writeGuestCartForCheckout,
} from "@/api/CartApi";
import {
  CHECKOUT_GUARD_TIMEOUT_MS,
  clearCartIfConsumed,
} from "@/libs/global-e/consumed-cart";
import { markGeCheckoutIntent } from "@/libs/global-e/checkout-marker";
import { FeatureFlagName, useIsFeatureOn } from "@/queries/feature-flag-query";
import { geCheckoutHref } from "@/libs/global-e/checkout-path";

// runCheckout(비-hook)에서 비배송국 차단을 호출 hook 의 catch 로 전달하기 위한 마커 에러.
// hook 쪽에서 instanceof 로 식별해 국가 선택 다이얼로그를 띄운다.
/**
 * 배송 상한(부피무게) 초과 — 바로구매(PDP)에서 GE 로 넘기기 **전에** 던진다.
 * 호출 hook 이 `instanceof` 로 받아 안내 다이얼로그를 띄운다(카트는 인라인 고지가 있지만
 * 바로구매는 클릭 후 비동기라 화면에 자리가 없다).
 */
export class OverweightShippingError extends Error {
  constructor() {
    super("OVERWEIGHT_SHIPPING");
    this.name = "OverweightShippingError";
  }
}

class NonShippableCountryError extends Error {
  constructor() {
    super(CHECKOUT_ERROR_CODE.NON_SHIPPABLE_COUNTRY);
    this.name = "NonShippableCountryError";
  }
}

/**
 * 서버(`/api/order/checkout`)가 게임 키 재고로 draft 를 거절했다(품절 · 모름). 호출 hook 이 `instanceof` 로 받아
 * 장바구니로 보낸다 — 진입 가드(`allGameLinesAvailable`)와 같은 길이다(docs/cart-game-key-stock-policy.md §2.5).
 */
class GameKeyStockError extends Error {
  constructor() {
    super(CHECKOUT_ERROR_CODE.GAME_KEY_STOCK_UNAVAILABLE);
    this.name = "GameKeyStockError";
  }
}

async function runCheckout({
  countryCode,
  currency,
  products,
  referralDiscountPercentile,
  token,
  router,
  hideCart,
  deviceItems,
  gameItems,
  geEnabled,
  entryPoint,
  direct,
}: {
  countryCode: string;
  currency: ReturnType<typeof useCurrency>;
  products: Awaited<ReturnType<typeof useProducts>>;
  referralDiscountPercentile: number;
  token?: string;
  router: ReturnType<typeof useRouter>;
  hideCart: () => void;
  deviceItems: CartItem[];
  gameItems: GameItem[];
  /** `begin_checkout` 이 어느 화면에서 시작됐는가 (#5292). 정의는 `cart-trigger.ts`. */
  entryPoint: CheckoutEntryPoint;
  // GlobalEEnabled flag (hook 에서 주입 — runCheckout 은 non-hook). false(prod)면 GE 분기 미진입.
  geEnabled: boolean;
  /**
   * 카트를 거치지 않은 바로구매인가(`useBuyTactSuitCheckout`). 카트 결제(`useShoppingBagCheckout`)는 false.
   * GE 경로에서 표식에 남긴다 — 그 token 이 소비됐을 때 **사지 않은 카트까지 비우지 않게**(`clearConsumedCart`).
   */
  direct: boolean;
}): Promise<{ redirected: boolean }> {
  // ★청구 통화는 **두 경로가 같은 축**을 쓴다 — `useCurrency()`.
  //   · 자체 결제: draft `currency` 가 되어 실제로 청구된다
  //   · GE: 청구는 GE 가 하지만 표시 통화가 이 값이라 **분석 라벨도 이 값**이어야 한다
  //     (flag ON 이면 유로존 EUR · GB GBP — `docs/analytics-funnel-policy.md` §1.1 표)
  //
  // ⚠️ 종전에는 `resolveChargeCurrency`(#5233)로 EUR/GBP → USD 강등을 했다. 자체 결제가
  // 유로존 통화를 못 받아서 필요했던 장치인데, 비-KR + flag ON 이 전부 GE 로 가는 지금은
  // **자체 결제 경로에 EUR/GBP 가 도달하지 않는다** — 아래 GE 분기가 먼저 반환한다.
  const chargeCurrency = currency;

  // ★품목·소계는 **GE 분기보다 먼저** 만들 수 있어야 한다 (#5328) — 순수 계산이라 네트워크가
  // 없고, GE 경로도 `begin_checkout` 을 쏘려면 이 값이 필요하다.
  // (카트 경로와 같은 함수 쌍이다 — `useCartWithProducts` 도
  //  `OrderService.generateOrderItem` → `OrderUtils.getPriceSummary` 로 총액을 만든다.
  //  같은 축이라야 `entry_point` 별 `value` 가 비교 가능하다.)
  const latestProductInfoList = CartService.getProductInfoList({
    cartItems: deviceItems,
    products: products.data ?? [],
  });

  const productsMap = new Map(
    (products.data ?? []).map((p) => [p.slug, p] as const),
  );

  /**
   * 카트 항목 → `OrderItemV2`.
   *
   * ★**가격을 못 구한 항목의 처리가 경로마다 다르다.** `generateOrderItem` 은 통화·국가에
   * 맞는 가격이 없으면 **`null`** 을 돌려주고, 처리는 여기서 정한다.
   *
   * | `onPriceError` | 경로      | 이유                                                       |
   * | -------------- | --------- | ---------------------------------------------------------- |
   * | `"throw"`      | 자체 결제 | 그 항목을 빼면 **과소청구**다. 결제를 멈춘다(`startCheckout` catch) |
   * | `"skip"`       | GE        | draft 를 안 만들어 청구에 영향이 없다. **결제를 막으면 안 된다** |
   *
   * ⚠️ GE 쪽을 `"throw"` 로 두면 **`begin_checkout` 을 올린 대가로 결제가 막힌다.**
   * flag ON + 유로존/GB 에서 `euPrice`/`gbPrice` 가 미등록이면(2026-09-17 prod 실측
   * 0/37 — `docs/analytics-funnel-policy.md` §1.1) `useCurrency()` 가 EUR/GBP 를 주고
   * 정확 매칭이 실패해 **모든 항목이** throw 한다. 계측을 위해 결제를 막는 것은 뒤바뀐
   * 우선순위다 — 그 상태에서는 §1.1 이 적은 대로 이벤트 쪽이 비는 게 맞다.
   */
  const buildOrderItems = (onPriceError: "throw" | "skip") =>
    deviceItems.reduce<OrderItemV2[]>((prev, item) => {
      const product = productsMap.get(item.slug);
      if (!product) {
        return prev;
      }

      const furtherDiscountPercentile = isReferralActiveForProduct(item.slug)
        ? referralDiscountPercentile
        : 0;

      const orderItem = OrderService.generateOrderItem({
        product: { ...product, ...item },
        currency: chargeCurrency,
        countryCode,
        furtherDiscountPercentile,
      });
      if (orderItem) return [...prev, orderItem];

      if (onPriceError === "throw") {
        throw new Error(
          `Price not found for product ${item.slug} in currency ${chargeCurrency}`,
        );
      }
      // 계측만 성기게 만들고 결제는 그대로 보낸다. 셋째 인자 = 가격을 못 찾은 통화(원인 추적용)
      console.warn(
        "Skipped unpriceable item for analytics.",
        item.sku,
        chargeCurrency,
      );
      return prev;
    }, []);

  /** 상품 소계(배송비 제외). 두 경로가 `begin_checkout` 의 `value` 를 여기서 만든다. */
  const subtotalOf = (items: OrderItemV2[]) =>
    OrderUtils.getPriceSummary({ items, gameItems }).totalSalePrice;

  /**
   * `begin_checkout`(GA4) + `InitiateCheckout`(Meta) 발화 — **두 경로가 공유한다.**
   *
   * ★함수로 묶는 이유: 자체 결제와 GE 는 `totalPayable` **하나만** 다르다. 두 자리에
   * 펼쳐 두면 한쪽만 고쳐져 `entry_point` 별로 payload 규약이 갈린다 — 그리고 그건
   * GA 에서 "값이 빔" 이 아니라 **"틀린 값이 들어감"** 으로 나타나 눈에 안 띈다
   * (`docs/analytics-funnel-policy.md` §1.2 와 같은 실패 모드).
   *
   * ⚠️ GA4 와 Meta 는 **같이** 움직인다 — 두 트래커가 같은 커밋에서 함께 사라진 전례가
   * 있어 정책 문서 §5-7 이 이를 체크리스트로 두고 있다.
   */
  const fireBeginCheckout = (totalPayable: number) => {
    // Send Google Analytics & Meta Pixel Event
    analyticsInstance?.clickCheckOutButtonEvent({
      cartItems: latestProductInfoList,
      gameItems,
      totalPayable,
      countryCode: countryCode || "Unknown",
      currency: chargeCurrency,
      entryPoint,
      // ⚠️ 버튼 이름이 아니라 **발화 시점의 실제 인증 상태**다 (#5292). PDP 의
      // `Account Checkout` 은 담고 로그인으로 보내기만 해서 그 클릭엔 이 이벤트가 없다 —
      // 돌아와 결제할 때 `account` 로 잡힌다.
      authMode: token ? "account" : "guest",
    });
    FbPixel.initCheckout(
      latestProductInfoList,
      gameItems,
      totalPayable,
      chargeCurrency,
    );
  };

  // GE 국제 체크아웃 라우팅 (buy-now/PDP 경로 — cart-sheet 버튼을 안 거치므로 여기서 처리, docs §4.4 #2).
  // **flag ON + 비-KR 이면 전부** 자체 결제(draft) 대신 /international-checkout 로 full-nav 하고,
  // GE 가 읽을 guest-cart(bh_cart_token)에 이 체크아웃 아이템을 직접 써둔다(uid 카트는 불변).
  //
  // ★2026-08-20 carrier 판정(`classify-by-sku`) 제거 — GE 협의로 **비-KR 은 전부 GE 결제**가 됐다.
  // 배터리 3개 이상은 GE Admin 에서 운송사만 **비햅틱스 자체배송(DDP)** 으로 바꾼다(결제는 그대로 GE).
  // 카트 조합으로 결제 경로가 갈리지 않으므로 판정할 것이 없다 — 국가만 본다.
  // (어떤 주문이 운송사 변경 대상인지는 **주문 수신 시점**(`SendOrderToMerchant`)에서 판별해
  //  물류에 알린다 — 사용자를 기다리게 할 이유가 없다.)
  //
  // prod flag OFF 면 이 블록에 진입하지 않는다.
  // ★술어는 `isGeRoutedFor` **공유** — 카트(`useGeRouted`)와 같은 함수다. non-hook 이라 훅을
  // 못 쓸 뿐 판정은 하나여야 한다(인라인 복제 시 두 경로가 갈린다 — marker.ts 주석).
  if (isGeRoutedFor(geEnabled, countryCode)) {
    // ★배송 상한 확인을 **여기서** 한다. 이 분기는 아래 배송비 조회보다 **먼저 반환**하므로
    // 그때 오는 `detail.weight` 를 못 본다 → GE 로 넘어간 뒤엔 이미 결제 화면이라 되돌리기가
    // 비싸다. 카트는 인라인 고지로 막지만 바로구매는 이 경로가 유일하다.
    // (물리 아이템이 없으면 조회를 건너뛴다 — 게임 단품은 무게가 없다.)
    //
    // ⚠️ **게스트 카트 쓰기와 병렬화하지 말 것.** 왕복 1회를 아끼려고 `writeGuestCartForCheckout`
    // 을 먼저 발사하면, 초과로 던져 **결제로 넘어가지 않는 경로에서도 게스트 카트가 덮인다.**
    // 그 문서는 버려지지 않는다 — `_app.tsx` 가 로그인 사용자에게 `mergeGuestCart()` 를 불러
    // uid 카트로 **병합**하므로, 사용자는 "무거워서 못 산다" 안내를 닫았을 뿐인데 그 구성이
    // 카트에 들어온다. 불변식: **결제로 넘어가지 않기로 결정한 경로는 게스트 카트를 바꾸지 않는다.**
    if (deviceItems.length > 0) {
      // ★**통화를 싣지 않는다** — 여기서 필요한 건 `detail.weight` 하나뿐이고, 무게는
      // 통화와 무관하게 온다. 통화를 실으면 EUR/GBP 는 우리 라우트가 `501` 을 내고
      // (환율 소스 없음) `ShippingApi` 가 `null` 을 돌려주므로 **무게까지 사라진다** →
      // 아래 `exceedsGeShippingWeight` 가 「모르면 통과」로 빠져 게이트가 통째로 꺼진다.
      // 카트 경로의 `resolveShippingCurrency` 와 같은 판단이다(`queries/shipping-query.ts`).
      //
      // ⚠️ 아래 **자체 결제** 경로의 호출(`chargeCurrency`)은 그대로 둔다 — 그쪽 값은
      // draft `shippingPrice` 가 되어 실제로 청구되므로 환산이 반드시 필요하다.
      const geShipping = await ShippingApi.calculateShippingPriceBySku(
        countryCode,
        deviceItems.map((i) => ({ sku: i.sku, qty: i.quantity })),
      );
      // 조회 실패(null)는 **막지 않는다** — 판정 불가로 구매를 차단하지 않는다는 규칙을
      // `exceedsGeShippingWeight` 와 공유한다(그 함수가 null 을 false 로 돌린다).
      if (exceedsGeShippingWeight({ geRouted: true, shipping: geShipping })) {
        throw new OverweightShippingError();
      }
    }

    // ★**GE 로 넘기기 전에 발화한다** (#5328). 이 분기가 자체 결제 경로보다 **먼저 반환**
    // 하므로 발화를 아래에 두면, flag ON 시 `pdp`·`checkout_page` 는 KR 만 세고
    // `cart_sheet` 는 전 세계를 센다 → `entry_point` 비교가 통째로 편향된다. 카트 버튼은
    // 이미 GE 분기 **위**에서 쏘고 있어(`cart-checkout-button.tsx`) 그쪽만 전수였다.
    //
    // ★`value` 에 **배송비가 없다.** 이 경로의 배송비는 GE 가 산정하고 우리는 모른다 —
    // 위 무게 조회는 통화를 안 실어 받은 값이라(주석 참조) 금액으로 쓸 수 없다. 카트의 GE
    // 경로도 같은 값이다: `shippingPrice` prop 이 0 으로 접혀 들어온다
    // (`queries/shipping-query.ts` 의 소비처 표 4행). 즉 **두 경로가 같은 규약**이다.
    //
    // ★무게 게이트 **뒤**에 둔다 — 초과로 막힌 클릭은 결제 시작이 아니다(사용자는 안내
    // 모달만 본다). 카트는 버튼이 인라인으로 disable 돼 애초에 여기까지 오지 않는다.
    //
    // ★**규약 — 발화 시점은 「결제로 넘어가기로 결정한 순간」이지 「도착을 확인한 순간」이
    // 아니다.** 아래 `writeGuestCartForCheckout` 는 던질 수 있고(`CartToken.ensure` 의 동기
    // throw — `CartApi.ts` 가 그 가능성을 적어 두고 있다. 실제 트리거는 비보안 컨텍스트에서
    // `globalThis.crypto` 가 없는 경우라 HTTPS 전용 prod 에서는 사실상 닫혀 있다), 그때는
    // **결제로 넘어가지 못한 클릭이 계측에 남는다.** 그걸 알고 여기 둔다:
    //   · 쓰기 **뒤**로 내리면 발화 직후 full-nav 라 비콘 시간이 사라진다(Meta 픽셀은 이미지
    //     요청이라 unload 에 약하다). 지금은 네트워크 왕복만큼 창이 확보된다.
    //   · 무게 게이트와 달리 이건 **사용자에게 보이는 거절이 아니라 인프라 실패**다.
    // ⚠️ 카트 경로는 같은 모양이지만 **창이 없다** — `flushCartSync` 는 큐의 이중 catch 로
    // 절대 reject 하지 않는다(`CartApi.ts` `enqueueSync`). (소비 token 가드에서의 비대칭은 아래에 따로 적는다.)
    fireBeginCheckout(subtotalOf(buildOrderItems("skip")));

    // ★이미 GE 주문으로 소비된 token 에 이 아이템을 쓰지 않는다(docs/global-e-storefront.md §4.7.3). 표식이 있을
    // 때만 1회 묻는다(없으면 요청 0회). 소비됐으면 그 token 을 정리한다 — 소비한 결제가 카트 결제였으면 카트를 비우고,
    // 바로구매였으면 token 만 교체한다(`clearConsumedCart`). 어느 쪽이든 아래 쓰기가 **새 token** 에 들어간다.
    // 정리 안에서 대기 중인 빈 sync 를 먼저 밀어내므로(`flushCartSync`) 그 빈 sync 가 새 token 의 쓰기를 덮지 않는다.
    // 이 의도는 그대로라 카트 버튼과 달리 **멈추지 않는다**(발화도 이미 나갔다 — 두 진입점의 두 번째 비대칭).
    // 판정 불가·시간 초과·예외는 종전대로 진행(reject 하지 않는다).
    await clearCartIfConsumed(CHECKOUT_GUARD_TIMEOUT_MS);

    // GE 가 GetCheckoutCartInfo 로 읽을 카트를 채운 뒤 full-nav (cart-checkout-button 과 동일 패턴).
    await writeGuestCartForCheckout(
      deviceItems.map((i) => ({ sku: i.sku, quantity: i.quantity })),
      gameItems.map((g) => ({
        gameResellId: g.gameResellId,
        platform: g.platform,
      })),
    );
    // 바로구매엔 쿠폰 선택 UI 가 없으므로 **자동 적용**으로 기록한다 — 이전에 카트에서 고른 "사용 안 함"이
    // 카트 문서에 남아 이 주문에 새지 않게. claim 도 여기서 기다린다(GE 의 첫 가격 조회보다 먼저 —
    // `prepareGeCheckoutCoupons` 주석). 쓰기 **뒤에** 부르는 이유는 카트 경로와 같다(같은 문서 lost-update).
    // 실패해도 막지 않는다: 서버 기본값이 자동 적용이다. 3s 상한(카트 flush 와 같은 값).
    await Promise.race([
      prepareGeCheckoutCoupons(false),
      new Promise((resolve) => setTimeout(resolve, 3000)),
    ]);
    // 이동 **직전**에 결제 종류를 표식에 남긴다 — 체크아웃 페이지 마운트는 종류를 모른다. 카트 결제도 `false` 로
    // **명시**한다(앞선 바로구매의 `direct` 가 새지 않게). 쓰기가 끝난 뒤여야 token 이 확정돼 있다
    // (`writeGuestCartForCheckout` 의 `CartToken.ensure`).
    markGeCheckoutIntent({ direct });
    hideCart();
    // 지금 로케일을 붙인다 — GE 가 결제 언어를 `<html lang>` 에서 읽는다(`geCheckoutHref`).
    window.location.href = geCheckoutHref(router.locale);
    return { redirected: true };
  }

  // ★여기 도달 = **자체 결제**(KR 또는 GE flag OFF). 위 블록에서 **비-KR + flag ON 은 전부**
  // GE 로 full-nav 하고 반환한다 — 그래서 `chargeCurrency` 가 KR 은 KRW, flag OFF 는 GE
  // 이전 규칙 그대로이고, 이 아래에 EUR/GBP 가 도달하지 않는다(위 선언부 주석).
  //
  // ★`"throw"` — 가격을 못 구하면 **여기서 멈춘다**(종전 동작). 이 배열이 draft `items` 가
  // 되므로 항목을 조용히 떨구면 그만큼 덜 청구된다.
  const orderItems = buildOrderItems("throw");

  // draft 는 소계만이 아니라 `subtotalAmount`·`discountAmount` 분해값도 쓴다.
  const priceSummary = OrderUtils.getPriceSummary({
    items: orderItems,
    gameItems,
  });

  const latestShippingType = OrderService.getShippingType({
    items: orderItems,
    gameItems,
  });

  let shippingPriceValue = 0;
  if (latestShippingType !== ShippingType.Virtual) {
    const calculatedShippingPrice =
      await ShippingApi.calculateShippingPriceBySku(
        countryCode,
        deviceItems.map((i) => ({ sku: i.sku, qty: i.quantity })),
        { currency: chargeCurrency },
      );

    if (calculatedShippingPrice === null) {
      throw new Error("Failed to calculate shipping price.");
    }

    shippingPriceValue = calculatedShippingPrice.price;
  }

  // ★자체 결제는 **배송비를 아는 시점**에 쏜다 — 이 값이 draft `totalAmount` 이자 실제
  // 청구액이다. GE 경로와 `value` 규약이 갈리는 지점이고, 그 사유는 위 분기 주석에 있다.
  const totalPayable = priceSummary.totalSalePrice + shippingPriceValue;

  fireBeginCheckout(totalPayable);

  const draft: Partial<OrderDraft> = {
    currency: chargeCurrency,
    includeShippingAndTax: countryCode === "US",
    items: orderItems,
    gameItems,
    shippingCountry: countryCode,
    shippingPrice: shippingPriceValue,
    subtotalAmount: priceSummary.totalOriginalPrice,
    discountAmount: priceSummary.totalDiscountAmount,
    totalAmount: priceSummary.totalSalePrice + shippingPriceValue,
    paymentMethod: "",
  };

  const { success, redirectUrl, code } = await OrderService.createOrderDraft(
    draft,
    token,
  );

  if (!success || !redirectUrl) {
    if (code === CHECKOUT_ERROR_CODE.NON_SHIPPABLE_COUNTRY) {
      throw new NonShippableCountryError();
    }
    if (code === CHECKOUT_ERROR_CODE.GAME_KEY_STOCK_UNAVAILABLE) {
      throw new GameKeyStockError();
    }
    // mgmt 가 이 통화 드래프트를 아직 수용하지 않는 경우(EUR/GBP). 일반 실패와 같은 UI 로
    // 처리하되, 원인이 로그·에러 메시지에 남게 해 운영 중 추적이 가능하게 한다(admin#1445).
    if (code === CHECKOUT_ERROR_CODE.CURRENCY_NOT_SUPPORTED_UPSTREAM) {
      throw new Error(
        `Order draft creation failed — currency ${chargeCurrency} not accepted upstream (admin#1445).`,
      );
    }
    throw new Error("Order draft creation failed.");
  }

  hideCart();
  await router.push(redirectUrl);
  return { redirected: true };
}

export function useShoppingBagCheckout({
  enabled = true,
  entryPoint,
}: {
  enabled?: boolean;
  /**
   * `begin_checkout` 의 진입점 (#5292). **호출처가 정한다** — 이 훅은 `/checkout` 페이지와
   * PDP 의 "담고 결제" 다이얼로그 **둘 다** 쓴다. 훅 안에서 추측하면 한쪽이 틀린다.
   */
  entryPoint: CheckoutEntryPoint;
}) {
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const { countryCode, setDialog } = useLocale();
  const cart = useCartWithProducts(enabled);
  const { gameItems } = cart;
  const geEnabled = !!useIsFeatureOn(FeatureFlagName.GlobalEEnabled);
  const currency = useCurrency();
  const { referralDiscountPercentile } = useReferralDiscount();
  const products = useProducts();
  const hideCart = useCartStore((cart) => cart.hideCart);
  const queryClient = useQueryClient();
  const router = useRouter();

  const startCheckout = async () => {
    if (isCheckingOut || !countryCode) return;
    // ★장바구니의 게임 줄이 **전부 판매 가능으로 확인되기 전엔** 결제로 넘기지 않고 장바구니로 보낸다 — 카트 버튼
    // (`cart-checkout-button`)과 같은 게이트다. 이 경로는 `gameItems`(확인된 것만)를 싣기 때문에, 그냥 진행하면
    // 「재고 확인 중」 게임이 **조용히 빠진 채** 결제된다(GE 경로는 게스트 카트를 그 목록으로 덮어쓴다). 막는 이유와
    // 줄별 상태는 장바구니가 보여준다(행의 「재고 확인 중」 · 결제 블록 안내). 판정은 `@/libs/cart/cart-game-stock`.
    if (!cart.allGameLinesAvailable) {
      await router.push("/cart");
      return;
    }
    // ★현재 통화로 가격이 없는 항목(ghost)이 남아 있어도 장바구니로 보낸다 — 카트 버튼의 `hasUnresolved` 와 같은
    // 판정(`CartService.getUnresolvedItems`)이다. 그대로 진행하면 자체 결제가 `Price not found` 로 멈추는데, 이 경로는
    // PDP(담고 결제 · 품절 결제)라 **화면에 아무것도 안 보인다**(`console.error` 만). 장바구니가 그 줄을 「이용할 수
    // 없는 품목」으로 보여주고 삭제를 유도한다. 예: 미국에서 EXOSHOCK 번들(KRW 미등록)을 담고 한국으로 바꾼 뒤 PDP 에서 결제.
    if (cart.unresolvedItems.length > 0) {
      await router.push("/cart");
      return;
    }
    setIsCheckingOut(true);
    let redirected = false;
    try {
      const latestCartState = getCartState();
      const latestCartItems = latestCartState.items as CartItem[];

      const result = await runCheckout({
        countryCode,
        currency,
        products,
        referralDiscountPercentile,
        token: await resolveCheckoutToken(queryClient),
        router,
        hideCart,
        deviceItems: latestCartItems,
        gameItems,
        geEnabled,
        entryPoint,
        direct: false,
      });
      redirected = result.redirected;
    } catch (err) {
      if (err instanceof OverweightShippingError) {
        // 배송 상한 초과 → 안내 모달. 드래프트는 만들지 않았다(GE 로 넘기기 전에 던졌다).
        useOverweightDialogStore.getState().show();
      } else if (err instanceof NonShippableCountryError) {
        // 비배송국 → 국가 선택 다이얼로그로 안내.
        setDialog("non-shippable-dialog");
      } else if (err instanceof GameKeyStockError) {
        // 서버가 게임 키 재고로 거절했다 — 위 진입 가드와 같은 길(장바구니)로 보낸다. 키 재고를 다시 물어 장바구니가 서버와
        // 같은 판정으로 줄별 이유를 보여주게 한다(클라가 본 재고는 최대 60초 전 값이라 갈릴 수 있다).
        void cart.refreshGameKeyStock();
        await router.push("/cart");
      } else {
        console.error("Checkout failed.", err);
      }
    } finally {
      if (!redirected) {
        setIsCheckingOut(false);
      }
    }
  };

  return {
    startCheckout,
    isCheckingOut: isCheckingOut,
    // canCheckout: !disabled,
  };
}

/**
 * PDP 레일의 "Checkout" 버튼 (장바구니를 거치지 않고 바로 결제로).
 *
 * @param offeredAccessorySkus **지금 화면에 떠 있는 액세서리의 SKU 집합.**
 *   스토어의 `accessories` 는 옵션/제품이 바뀌어도 자동 정리되지 않아, 목록에서
 *   사라진 항목이 `selected` 로 남는다. 그대로 담으면 화면에 없는 물건이 결제된다
 *   (실측: 글러브 사이즈 S→L, TactSuit Air→Camo).
 *
 *   ⚠️ 스토어의 `product` 로 여기서 직접 계산하면 **안 된다** — 그건 `displayProduct`
 *   라 번들 선택 시 번들 제품이고, `accessoryFor` 가 안 걸려 집합이 비어 정당한
 *   액세서리까지 빠진다. 실효 제품을 아는 호출처(템플릿)가 만들어 넘긴다.
 */
export function useBuyTactSuitCheckout(
  offeredAccessorySkus: Set<string>,
  /** `begin_checkout` 의 진입점 (#5292). 이 훅은 PDP 전용이지만 값은 호출처가 준다. */
  entryPoint: CheckoutEntryPoint,
) {
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const { countryCode, setDialog } = useLocale();
  const {
    product,
    // gameResells,
    accessories,
    options,
    getSku,
  } = useBuyTactSuit((s) => ({
    getSku: s.getSku,
    product: s.product,
    options: s.options,
    // gameResells: s.gameResells,
    accessories: s.accessories,
  }));
  const currency = useCurrency();
  const { referralDiscountPercentile } = useReferralDiscount();
  const products = useProducts();
  const hideCart = useCartStore((cart) => cart.hideCart);
  const queryClient = useQueryClient();
  const router = useRouter();
  const geEnabled = !!useIsFeatureOn(FeatureFlagName.GlobalEEnabled);

  /**
   * `direct` — GE 결제 표식의 결제 종류(기본 `true` = 바로구매: 결제 뒤 카트를 남긴다). 호출부가 **방금 고른 구성을
   * 카트에 담고** 이 훅으로 결제하면 카트 = 결제 구성이라 `false` 로 넘긴다 — 아니면 결제 뒤에도 산 물건이 카트에
   * 남는다(docs/global-e-storefront.md §4.7.3 「정리 = clearConsumedCart()」).
   */
  const startCheckout = async ({
    direct = true,
  }: { direct?: boolean } = {}) => {
    if (isCheckingOut || !countryCode) return;
    setIsCheckingOut(true);
    let redirected = false;
    const sku = getSku();
    if (!product || !sku) {
      setIsCheckingOut(false);
      return;
    }

    const productBySku = ProductUtil.getProductBySku(products.data ?? [], sku);
    const deviceItems = [
      // item이 여러개일 때 앞에 것부터 추가되기 때문에
      // accessory가 먼저 추가되어야 0번 아이템이 product가 됨
      // 파우치 2개 → 세트: 카트 담기 경로(`useAddAccessories`)와 같은 함수로 접는다.
      ...packSetLines(
        accessories
          .filter((acc) =>
            ProductUtil.isChargeableAccessory(acc, offeredAccessorySkus),
          )
          .map((acc) => ({
            slug: acc.slug as ProductSlug,
            sku: acc.sku,
            quantity: acc.quantity,
            options: [],
          })),
        products.data ?? [],
        { currency, countryCode },
      ),
      {
        slug: productBySku?.slug ?? product.slug,
        sku,
        quantity: 1,
        options,
      },
    ].filter((item) => !!item);
    try {
      const result = await runCheckout({
        countryCode,
        currency,
        products,
        referralDiscountPercentile,
        token: await resolveCheckoutToken(queryClient),
        router,
        hideCart,
        deviceItems: deviceItems as CartItem[],
        gameItems: [],
        geEnabled,
        entryPoint,
        direct,
      });
      redirected = result.redirected;
    } catch (err) {
      if (err instanceof OverweightShippingError) {
        // 배송 상한 초과 → 안내 모달. 드래프트는 만들지 않았다(GE 로 넘기기 전에 던졌다).
        useOverweightDialogStore.getState().show();
      } else if (err instanceof NonShippableCountryError) {
        // 비배송국 → 국가 선택 다이얼로그로 안내.
        setDialog("non-shippable-dialog");
      } else {
        console.error("Checkout failed.", err);
      }
    } finally {
      if (!redirected) {
        setIsCheckingOut(false);
      }
    }
  };

  return {
    startCheckout,
    isCheckingOut: isCheckingOut,
    // canCheckout: !disabled,
  };
}
